import { AlertTriangle, Inbox, SearchX } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "./Button";
import { cn } from "./cn";

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
  kind?: "empty" | "search";
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  kind = "empty",
  className
}: EmptyStateProps) {
  const Icon = kind === "search" ? SearchX : Inbox;

  return (
    <div className={cn("flex flex-col items-center px-6 py-12 text-center", className)}>
      <span className="mb-4 grid size-12 place-items-center rounded-full bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
        <Icon className="size-6" aria-hidden="true" />
      </span>
      <h2 className="text-base font-semibold text-zinc-950 dark:text-white">{title}</h2>
      {description ? (
        <p className="mt-1 max-w-md text-sm leading-6 text-zinc-500 dark:text-zinc-400">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: string;
  requestId?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = "Não foi possível carregar os dados",
  description = "Tente novamente. Se o problema continuar, informe o identificador da requisição ao suporte.",
  requestId,
  onRetry,
  className
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn("flex flex-col items-center px-6 py-12 text-center", className)}
    >
      <span className="mb-4 grid size-12 place-items-center rounded-full bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400">
        <AlertTriangle className="size-6" aria-hidden="true" />
      </span>
      <h2 className="text-base font-semibold text-zinc-950 dark:text-white">{title}</h2>
      <p className="mt-1 max-w-lg text-sm leading-6 text-zinc-500 dark:text-zinc-400">
        {description}
      </p>
      {requestId ? (
        <p className="mt-3 font-mono text-xs text-zinc-500">
          Código da solicitação: <span className="select-all">{requestId}</span>
        </p>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" className="mt-5" onClick={onRetry}>
          Tentar novamente
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      className={cn("block animate-pulse rounded-md bg-zinc-200 dark:bg-zinc-800", className)}
      aria-hidden="true"
    />
  );
}

export function LoadingState({ label = "Carregando" }: { label?: string }) {
  return (
    <div role="status" className="space-y-3 p-5">
      <span className="sr-only">{label}</span>
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-4/5" />
    </div>
  );
}
